import { motion } from '@carbon/motion';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import { MotionDuration, MotionMode, MotionName } from '@upright/ui/common/generic/motion/types';
import { themeGet } from '@upright/ui/common/generic/theme/custom-theme';
import { Link } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';
import { getTextStyle } from '../../../../content/texts/bou-text/text-style';
import { BouColor } from '../../../../global/bou-colors';

export const navFastTransition = `${MotionDuration.FAST1_70ms}ms ${motion(MotionName.STANDARD, MotionMode.PRODUCTIVE)}`;

const fastAnimation = `${MotionDuration.FAST2_110ms}ms`;

const scaleIn = keyframes({
  from: { transform: 'rotateX(-30deg) scale(0.9)', opacity: 0 },
  to: { transform: 'rotateX(0deg) scale(1)', opacity: 1 },
});

const scaleOut = keyframes({
  from: { transform: 'rotateX(0deg) scale(1)', opacity: 1 },
  to: { transform: 'rotateX(-10deg) scale(0.95)', opacity: 0 },
});

const growIn = keyframes({
  from: { transform: 'translateY(100%)' },
  to: { transform: 'translateY(0) }' },
});

const shrinkOut = keyframes({
  from: { transform: 'translateY(0)' },
  to: { transform: 'translateY(100%)' },
});

// Todo: find a suitable place for animations in the UI and add there. E.g. helpers/animation.helpers.ts or something.
export function navAnimateScaleIn() {
  return css`
    animation: ${scaleIn} ${fastAnimation} ${motion(MotionName.ENTRANCE, MotionMode.PRODUCTIVE)};
  `;
}

export function navAnimateScaleOut() {
  return css`
    animation: ${scaleOut} ${fastAnimation} ${motion(MotionName.EXIT, MotionMode.PRODUCTIVE)};
  `;
}

export function navAnimateGrowIn() {
  return css`
    animation: ${growIn} ${fastAnimation} ${motion(MotionName.EXIT, MotionMode.PRODUCTIVE)};
  `;
}

export function navAnimateShrinkOut() {
  return css`
    animation: ${shrinkOut} ${fastAnimation} ${motion(MotionName.EXIT, MotionMode.PRODUCTIVE)};
  `;
}

export const navItemStyles = `
${getTextStyle({ size: 'xxxs' })}
font-weight: 600;
padding: 0.5em 0.8em;
color: ${BouColor.WHITE};
transition: color ${navFastTransition};
&:hover {
  color: ${BouColor.TOOL_HOVER_LIGHT_GRAY};
};
`;

export const navBorderStyles = `
border: 1px solid ${BouColor.PLATFORM_BORDER_GRAY_DARK};
border-radius: 4px;
box-shadow: 0px 4px 20px rgba(0, 0, 0, 5%);
`;

const linkStyles = `
  all: unset;
  cursor: pointer;
`;

export const Item = styled.div`
  ${linkStyles};
  ${navItemStyles};
`;

export const InternalLinkItem = styled(Link)`
  ${linkStyles};
`;

export const ExternalLinkItem = styled.a`
  ${linkStyles};
`;

/*
 **** Radix stuff ****
 */

export const MenuRoot = styled(NavigationMenu.Root)`
  position: relative;
  display: flex;
`;

export const MenuList = styled(NavigationMenu.List)`
  all: unset;
  display: flex;
  justify-content: center;
  gap: 8px;
  list-style: none;
`;

export const MenuItemTriggerWrapper = styled(NavigationMenu.Item)``;

export const MenuTrigger = styled(NavigationMenu.Trigger)`
  all: unset;
  ${navItemStyles};
  display: flex;
  align-items: center;
  gap: 4px;

  &[data-state='open'] {
    > svg {
      transition: transform ${navFastTransition};
      transform: rotate(180deg);
    }
  }
`;

export const MenuContent = styled(NavigationMenu.Content)`
  position: absolute;
  top: 0;
  left: -25%;
  right: 0;
  background-color: ${BouColor.PLATFORM_BG_GRAY_DARK};
  border-radius: 4px;
  padding-top: ${themeGet('space.2')};
  padding-bottom: ${themeGet('space.2')};
`;

export const ContentList = styled.ul`
  list-style: none;
  width: 100%;
  padding: 0;
`;

export const ListItem = styled.li`
  transition: background-color ${navFastTransition};
  color: ${themeGet('colors.fg.onEmphasis')};
  &:hover {
    background-color: ${BouColor.PLATFORM_BG_GRAY};
  }
  &:last-child {
    border-radius: 0 0 4px 4px;
  }
`;

export const ListItemInner = styled.div`
  font-weight: 500;
  font-size: ${themeGet('fontSizes.0')};
  padding: 6px 16px;
  display: flex;
  gap: 8px;
  align-items: center;
`;

export const StyledIndicator = styled(NavigationMenu.Indicator)`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  height: 9px;
  overflow: hidden;
  z-index: 1;
  transition: transform ${MotionDuration.MODERATE1_150ms}ms ${motion(MotionName.STANDARD, MotionMode.EXPRESSIVE)};
  &[data-state='visible'] {
    ${navAnimateGrowIn}
  }
  &[data-state='hidden'] {
    ${navAnimateShrinkOut}
  }
`;

export const Arrow = styled.div`
  position: relative;
  top: 70%;
  background-color: ${BouColor.PLATFORM_BG_GRAY_DARK};
  width: 10px;
  height: 10px;
  transform: rotate(45deg);
`;

export const MenuViewport = styled(NavigationMenu.Viewport)`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  min-width: 180px;
  height: var(--radix-navigation-menu-viewport-height);
  background-color: ${BouColor.PLATFORM_BG_GRAY_DARK};
  ${navBorderStyles}
  &[data-state='open'] {
    ${navAnimateScaleIn}
  }
  &[data-state='closed'] {
    ${navAnimateScaleOut}
  }
`;
